import { getApiServiceName, SERVICE_OPTIONS } from 'constants/serviceDistribution';
import { VideoAssetSet } from 'content/library/types/contentVideoType';
import { videoService } from 'service/api/videoService';

export interface DistributionStatusResult {
  [serviceName: string]: {
    enabled: boolean;
    id?: number;
    status?: string;
    auto_publish?: boolean;
    content_consumer_name?: string;
    created?: string;
    modified?: string;
  };
}

export interface DistributionReflectionResult {
  serviceStatusMessages: string[];
}

export interface VideoAssetValidationResult {
  // 1-1. Video Asset Check
  hasVideoAsset: boolean;
  totalCount: number;
  primaryCount: number;
  isValidPrimaryCount: boolean;
  primaryVideoIdCount: number; // Number of video_ids that have primary: true
  primaryAsset: {
    id: number;
    video: number;
    service_name: string;
  } | null;
  // 1-2. Content Thumbnail & Poster Check
  thumbnailAndPoster: {
    hasDefaultVideoThumbnail: boolean;
    defaultVideoThumbnailCount: number;
    contentThumbnailDuplicates: Array<{
      language: string;
      orientation: string;
      count: number;
      thumbnails: Array<{
        id: number;
        language?: string;
        orientation?: string;
      }>;
    }>;
  } | null;
  // 1-3. Stream Manifests Check (grouped by service_name)
  streamManifests: Record<
    string,
    {
      count: number;
      manifests: Array<{
        type: string;
        preset: string;
        status: string;
        stream_url: string;
      }>;
      statusCounts: Record<string, number>;
    }
  > | null;
  // 1-4. Video Stream Manifests Check
  videoStreamManifests: {
    count: number;
    manifests: Array<{
      type: string;
      preset: string;
      status: string;
      stream_url: string;
    }>;
    statusCounts: Record<string, number>;
  } | null;
  // 1-5. Service Auto Distribution
  serviceAutoDistributions: {
    count: number;
    distributions: Array<{
      service_name: string;
      auto_publish: boolean;
      subtitle_langs: string[];
    }>;
  };
  // 1-6. Content Distribution
  distributions: {
    count: number;
    distributions: Array<{
      id: number;
      content_consumer_name: string;
      status: string;
      auto_publish: boolean;
    }>;
  };
  // 1-7. Distribution Episode Sets
  distributionSets: {
    count: number;
    sets: Array<{
      service_name: string;
      episode_id: number;
      content_id: number;
    }>;
  };
  // 1-8. Service Distribution Check
  distributionStatus: DistributionStatusResult;
  distributionReflection: DistributionReflectionResult;
  validationIssues: string[];
}

export interface DistributionValidationResult {
  validationIssues: string[];
}

/**
 * Validates all video asset related checks (1-1 through 1-7)
 * Uses facade pattern with private functions for each check
 */
export const validateVideoAssetPrimary = async (
  contentVideoAssets: VideoAssetSet[],
  contentData: any,
  seasonData?: any,
  seriesData?: any
): Promise<VideoAssetValidationResult> => {
  const validationIssues: string[] = [];

  // 1-1. Video Asset Connection Check
  const videoAssetCheck = checkVideoAssetConnection(contentVideoAssets, validationIssues);

  // 1-2. Content Thumbnail & Poster Check
  const thumbnailAndPosterCheck = await checkThumbnailAndPoster(
    videoAssetCheck.primaryAsset?.video,
    contentData,
    validationIssues
  );

  // 1-3. Stream Manifests Check
  // Find all assets with is_primary=true (or primary=true or is_default=true)
  const primaryAssets = contentVideoAssets.filter(
    asset =>
      (asset as any).is_primary === true || asset.primary === true || asset.is_default === true
  );
  const streamManifestsCheck = checkStreamManifests(primaryAssets, validationIssues);

  // 1-4. Video Stream Manifests Check
  const videoStreamManifestsCheck = await checkVideoStreamManifests(
    videoAssetCheck.primaryAsset?.video,
    validationIssues
  );

  // 1-5, 1-6, 1-7. Content Distribution Checks
  // Handle service_auto_distribution_inherit logic
  let serviceAutoDistributions = (contentData as any).service_auto_distributions || [];
  if ((contentData as any).service_auto_distribution_inherit && seasonData) {
    serviceAutoDistributions = seasonData.service_auto_distributions || [];
    // If season also inherits, use series distributions
    if (seasonData.service_auto_distribution_inherit && seriesData) {
      serviceAutoDistributions = seriesData.service_auto_distributions || [];
    }
  }

  const distributions = (contentData as any).distributions || [];
  const distributionSets = (contentData as any).distribution_sets || [];

  const serviceAutoDistributionsCheck = checkServiceAutoDistributions(
    serviceAutoDistributions,
    validationIssues
  );
  const distributionsCheck = checkContentDistributions(distributions, validationIssues);
  const distributionSetsCheck = checkDistributionSets(distributionSets, validationIssues);

  // 1-8. Service Distribution Check
  const distributionStatusCheck = checkContentDistributionStatus(contentData);
  const distributionReflectionCheck = checkContentDistributionReflection(
    distributionStatusCheck,
    contentData
  );

  // Validate relationships between distributions
  validateDistributionRelationships(
    serviceAutoDistributions,
    distributions,
    distributionSets,
    validationIssues
  );

  return {
    ...videoAssetCheck,
    thumbnailAndPoster: thumbnailAndPosterCheck,
    streamManifests: streamManifestsCheck,
    videoStreamManifests: videoStreamManifestsCheck,
    serviceAutoDistributions: serviceAutoDistributionsCheck,
    distributions: distributionsCheck,
    distributionSets: distributionSetsCheck,
    distributionStatus: distributionStatusCheck,
    distributionReflection: distributionReflectionCheck,
    validationIssues,
  };
};

/**
 * 1-1. Video Asset Connection Check
 * - Only one video_id should have primary: true
 * - All assets in that video_id group must have primary: true
 * - Other video_id groups should not have any primary: true
 */
const checkVideoAssetConnection = (
  contentVideoAssets: VideoAssetSet[],
  validationIssues: string[]
): {
  hasVideoAsset: boolean;
  totalCount: number;
  primaryCount: number;
  isValidPrimaryCount: boolean;
  primaryVideoIdCount: number;
  primaryAsset: {
    id: number;
    video: number;
    service_name: string;
  } | null;
} => {
  const hasVideoAsset = contentVideoAssets.length > 0;

  // Group contentVideoAssets by video_id
  const videoIdGroups = contentVideoAssets.reduce(
    (acc, asset) => {
      const videoId = asset.video;
      if (videoId !== null && videoId !== undefined) {
        if (!acc[videoId]) {
          acc[videoId] = [];
        }
        acc[videoId].push(asset);
      }
      return acc;
    },
    {} as Record<number, VideoAssetSet[]>
  );

  // Find video_ids that have primary: true
  const primaryVideoIds: number[] = [];
  const primaryByVideoId: Record<number, VideoAssetSet[]> = {};

  Object.keys(videoIdGroups).forEach(videoIdStr => {
    const videoId = Number(videoIdStr);
    const group = videoIdGroups[videoId];
    const primaryInGroup = group.filter(asset => asset.primary || asset.is_default);

    if (primaryInGroup.length > 0) {
      primaryVideoIds.push(videoId);
      primaryByVideoId[videoId] = primaryInGroup;
    }
  });

  // Validate: Only one video_id should have primary assets
  let isValidPrimaryCount = false;
  let primaryCount = 0;
  let primaryAsset: VideoAssetSet | null = null;
  const primaryVideoIdCount = primaryVideoIds.length;

  if (primaryVideoIds.length === 0) {
    validationIssues.push('No primary video asset found.');
  } else if (primaryVideoIds.length > 1) {
    validationIssues.push(
      `Multiple video_ids have primary assets: ${primaryVideoIds.join(', ')}. ` +
        `Only one video_id should have primary assets.`
    );
  } else {
    // Only one video_id has primary assets - this is the main validation
    const primaryVideoId = primaryVideoIds[0];
    const primaryGroup = videoIdGroups[primaryVideoId];
    const primaryInGroup = primaryByVideoId[primaryVideoId];

    // Primary video_id count is 1, so it's valid
    isValidPrimaryCount = true;
    primaryCount = primaryInGroup.length;
    primaryAsset = primaryInGroup[0];

    // Additional checks (these are warnings but don't invalidate the primary count)
    // Check if all assets in the primary video_id group have primary: true
    const allPrimary = primaryGroup.every(asset => asset.primary || asset.is_default);
    if (!allPrimary) {
      validationIssues.push(
        `Not all assets in video_id ${primaryVideoId} have primary: true. ` +
          `All assets in the primary video_id group must have primary: true.`
      );
    }

    // Check if other video_id groups have any primary: true
    Object.keys(videoIdGroups).forEach(videoIdStr => {
      const videoId = Number(videoIdStr);
      if (videoId !== primaryVideoId) {
        const group = videoIdGroups[videoId];
        const hasPrimary = group.some(asset => asset.primary || asset.is_default);
        if (hasPrimary) {
          validationIssues.push(
            `Video_id ${videoId} should not have primary assets, but found primary: true.`
          );
        }
      }
    });
  }

  return {
    hasVideoAsset,
    totalCount: contentVideoAssets.length,
    primaryCount,
    isValidPrimaryCount,
    primaryVideoIdCount,
    primaryAsset: primaryAsset
      ? {
          id: primaryAsset.id!,
          video: primaryAsset.video!,
          service_name: primaryAsset.service_name,
        }
      : null,
  };
};

/**
 * 1-2. Content Thumbnail & Poster Check
 * - Check if primary video has at least one default thumbnail (is_default: true)
 * - If content_type is 'movie', check for duplicate content thumbnails
 */
const checkThumbnailAndPoster = async (
  primaryVideoId: number | undefined,
  contentData: any,
  validationIssues: string[]
): Promise<{
  hasDefaultVideoThumbnail: boolean;
  defaultVideoThumbnailCount: number;
  contentThumbnailDuplicates: Array<{
    language: string;
    orientation: string;
    count: number;
    thumbnails: Array<{
      id: number;
      language?: string;
      orientation?: string;
    }>;
  }>;
} | null> => {
  if (!primaryVideoId) {
    return null;
  }

  try {
    // Fetch video data to get video_thumbnails
    const videoData = await videoService.fetchById(primaryVideoId);
    const videoThumbnails = videoData.video_thumbnails || [];

    // Check for default video thumbnails
    const defaultVideoThumbnails = videoThumbnails.filter(
      (thumb: any) => thumb.is_default === true
    );
    const hasDefaultVideoThumbnail = defaultVideoThumbnails.length > 0;
    const defaultVideoThumbnailCount = defaultVideoThumbnails.length;

    if (!hasDefaultVideoThumbnail) {
      validationIssues.push(
        `Primary video (ID: ${primaryVideoId}) does not have any default thumbnail (is_default: true).`
      );
    }

    // Check content thumbnails for duplicates (only for movie type)
    const contentThumbnailDuplicates: Array<{
      language: string;
      orientation: string;
      count: number;
      thumbnails: Array<{
        id: number;
        language?: string;
        orientation?: string;
      }>;
    }> = [];

    const contentType = contentData.content_type;
    if (contentType === 'movie' && contentData.thumbnails) {
      const thumbnails = contentData.thumbnails || [];
      const thumbnailMap = new Map<string, any[]>();

      // Group thumbnails by language and orientation
      thumbnails.forEach((thumb: any) => {
        const language = thumb.language || 'unknown';
        const orientation = thumb.orientation || 'unknown';
        const key = `${language}-${orientation}`;

        if (!thumbnailMap.has(key)) {
          thumbnailMap.set(key, []);
        }
        thumbnailMap.get(key)!.push(thumb);
      });

      // Find duplicates (more than 1 thumbnail with same language and orientation)
      thumbnailMap.forEach((thumbList, key) => {
        if (thumbList.length > 1) {
          const [language, orientation] = key.split('-');
          contentThumbnailDuplicates.push({
            language,
            orientation,
            count: thumbList.length,
            thumbnails: thumbList.map((thumb: any) => ({
              id: thumb.id,
              language: thumb.language,
              orientation: thumb.orientation,
            })),
          });
        }
      });

      if (contentThumbnailDuplicates.length > 0) {
        validationIssues.push(
          `Content has duplicate thumbnails: ${contentThumbnailDuplicates.length} duplicate groups found.`
        );
      }
    }

    return {
      hasDefaultVideoThumbnail,
      defaultVideoThumbnailCount,
      contentThumbnailDuplicates,
    };
  } catch (error) {
    console.error('Failed to check thumbnails and posters:', error);
    validationIssues.push(`Failed to fetch video data for thumbnail check.`);
    return null;
  }
};

/**
 * 1-3. Stream Manifests Check
 * Groups primary assets by service_name and validates stream manifests for each service
 */
const checkStreamManifests = (
  primaryAssets: VideoAssetSet[],
  _validationIssues: string[]
): Record<
  string,
  {
    count: number;
    manifests: Array<{
      type: string;
      preset: string;
      status: string;
      stream_url: string;
    }>;
    statusCounts: Record<string, number>;
  }
> | null => {
  if (!primaryAssets || primaryAssets.length === 0) {
    return null;
  }

  // Group primary assets by service_name (exclude empty service_name)
  const assetsByService = primaryAssets.reduce(
    (acc, asset) => {
      const serviceName = asset.service_name;
      // Exclude assets with empty or undefined service_name
      if (!serviceName || serviceName.trim() === '') {
        return acc;
      }
      if (!acc[serviceName]) {
        acc[serviceName] = [];
      }
      acc[serviceName].push(asset);
      return acc;
    },
    {} as Record<string, VideoAssetSet[]>
  );

  // Validate stream manifests for each service
  const result: Record<
    string,
    {
      count: number;
      manifests: Array<{
        type: string;
        preset: string;
        status: string;
        stream_url: string;
      }>;
      statusCounts: Record<string, number>;
    }
  > = {};

  Object.keys(assetsByService).forEach(serviceName => {
    const serviceAssets = assetsByService[serviceName];

    // Collect all manifests from all assets in this service
    const allManifests: any[] = [];
    serviceAssets.forEach(asset => {
      if (asset.stream_manifests && asset.stream_manifests.length > 0) {
        allManifests.push(...asset.stream_manifests);
      }
    });

    if (allManifests.length > 0) {
      const statusCounts = allManifests.reduce((acc: Record<string, number>, m: any) => {
        acc[m.status] = (acc[m.status] || 0) + 1;
        return acc;
      }, {});

      result[serviceName] = {
        count: allManifests.length,
        manifests: allManifests.map((m: any) => ({
          type: m.type,
          preset: m.preset,
          status: m.status,
          stream_url: m.stream_url,
        })),
        statusCounts,
      };
    }
  });

  return Object.keys(result).length > 0 ? result : null;
};

/**
 * 1-3. Video Stream Manifests Check
 * Checks stream_manifests from the primary video_id
 */
const checkVideoStreamManifests = async (
  primaryVideoId: number | undefined,
  _validationIssues: string[]
): Promise<{
  primaryVideoId: number;
  count: number;
  manifests: Array<{
    type: string;
    preset: string;
    status: string;
    stream_url: string;
  }>;
  statusCounts: Record<string, number>;
} | null> => {
  if (!primaryVideoId) {
    return null;
  }

  try {
    const videoData = await videoService.fetchById(primaryVideoId);
    const streamManifests = videoData.stream_manifests || [];

    if (streamManifests.length === 0) {
      return null;
    }

    const statusCounts = streamManifests.reduce((acc: Record<string, number>, m: any) => {
      acc[m.status] = (acc[m.status] || 0) + 1;
      return acc;
    }, {});

    return {
      primaryVideoId: primaryVideoId,
      count: streamManifests.length,
      manifests: streamManifests.map((m: any) => ({
        type: m.type,
        preset: m.preset,
        status: m.status,
        stream_url: m.stream_url_full || m.stream_url || '',
      })),
      statusCounts,
    };
  } catch (error) {
    console.error('Failed to fetch video stream manifests:', error);
    return null;
  }
};

/**
 * 1-4. Service Auto Distribution Check
 */
const checkServiceAutoDistributions = (
  serviceAutoDistributions: any[],
  _validationIssues: string[]
): {
  count: number;
  distributions: Array<{
    service_name: string;
    auto_publish: boolean;
    subtitle_langs: string[];
  }>;
} => {
  return {
    count: serviceAutoDistributions.length,
    distributions: serviceAutoDistributions.map((d: any) => ({
      service_name: d.service_name,
      auto_publish: d.auto_publish,
      subtitle_langs: d.subtitle_langs || [],
    })),
  };
};

/**
 * 1-4. Content Distribution Check
 */
const checkContentDistributions = (
  distributions: any[],
  _validationIssues: string[]
): {
  count: number;
  distributions: Array<{
    id: number;
    content_consumer_name: string;
    status: string;
    auto_publish: boolean;
  }>;
} => {
  return {
    count: distributions.length,
    distributions: distributions.map((d: any) => ({
      id: d.id,
      content_consumer_name: d.content_consumer_name,
      status: d.status,
      auto_publish: d.auto_publish,
    })),
  };
};

/**
 * 1-5. Distribution Episode Sets Check
 */
const checkDistributionSets = (
  distributionSets: any[],
  _validationIssues: string[]
): {
  count: number;
  sets: Array<{
    service_name: string;
    episode_id: number;
    content_id: number;
  }>;
} => {
  return {
    count: distributionSets.length,
    sets: distributionSets.map((d: any) => ({
      service_name: d.service_name,
      episode_id: d.episode_id,
      content_id: d.content_id,
    })),
  };
};

/**
 * Validates relationships between service_auto_distribution, distribution, and distribution_set
 */
const validateDistributionRelationships = (
  serviceAutoDistributions: any[],
  distributions: any[],
  distributionSets: any[],
  validationIssues: string[]
): void => {
  SERVICE_OPTIONS.forEach(service => {
    const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
    const apiServiceName = getApiServiceName(service.name, currentEnv);

    const autoDist = serviceAutoDistributions.find((d: any) => d.service_name === apiServiceName);
    const dist = distributions.find((d: any) => d.content_consumer_name === service.apiName);
    // distributionSets의 service_name은 apiName 또는 name일 수 있음
    const distSet = distributionSets.find(
      (d: any) => d.service_name === service.apiName || d.service_name === service.name
    );

    // If service_auto_distribution is enabled but distribution doesn't exist, it's a problem
    if (autoDist && !dist) {
      validationIssues.push(
        `${service.name}: service_auto_distribution is configured but distribution does not exist.`
      );
    }

    // If distribution exists but distribution_set doesn't exist, it's a problem
    if (dist && !distSet) {
      validationIssues.push(
        `${service.name}: distribution exists but distribution_set does not exist.`
      );
    }
  });
};

/**
 * Validates service_auto_distribution and distribution relationships
 */
export const validateServiceAutoDistribution = (
  serviceAutoDistributions: any[],
  distributions: any[],
  distributionSets: any[]
): DistributionValidationResult => {
  const validationIssues: string[] = [];

  SERVICE_OPTIONS.forEach(service => {
    const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
    const apiServiceName = getApiServiceName(service.name, currentEnv);

    const autoDist = serviceAutoDistributions.find((d: any) => d.service_name === apiServiceName);
    const dist = distributions.find((d: any) => d.content_consumer_name === service.apiName);
    // distributionSets의 service_name은 apiName 또는 name일 수 있음
    const distSet = distributionSets.find(
      (d: any) => d.service_name === service.apiName || d.service_name === service.name
    );

    // If service_auto_distribution is enabled but distribution doesn't exist, it's a problem
    if (autoDist && !dist) {
      validationIssues.push(
        `${service.name}: service_auto_distribution is configured but distribution does not exist.`
      );
    }

    // If distribution exists but distribution_set doesn't exist, it's a problem
    if (dist && !distSet) {
      validationIssues.push(
        `${service.name}: distribution exists but distribution_set does not exist.`
      );
    }
  });

  return {
    validationIssues,
  };
};

/**
 * 1-8. Checks distribution status for a content
 */
const checkContentDistributionStatus = (contentData: any): DistributionStatusResult => {
  const distributions = contentData.distributions || [];
  const result: DistributionStatusResult = {};

  SERVICE_OPTIONS.forEach(service => {
    const dist = distributions.find((d: any) => d.content_consumer_name === service.apiName);
    result[`${service.name}`] = dist
      ? {
          enabled: true,
          id: dist.id,
          status: dist.status,
          auto_publish: dist.auto_publish,
          content_consumer_name: dist.content_consumer_name,
          created: dist.created,
          modified: dist.modified,
        }
      : { enabled: false };
  });

  return result;
};

/**
 * 1-8. Checks if newly modified content has been reflected in distributions
 * Content level includes Amasian in the check
 */
const checkContentDistributionReflection = (
  distributionStatus: DistributionStatusResult,
  contentData: any
): DistributionReflectionResult => {
  const serviceStatusMessages: string[] = [];

  SERVICE_OPTIONS.forEach(service => {
    const distStatus = distributionStatus[service.name];
    if (distStatus?.enabled) {
      // If status is not COMPLETED, content is not reflected
      if (distStatus.status !== 'COMPLETED') {
        serviceStatusMessages.push(
          `${service.name}: Newly modified content has not been reflected`
        );
      } else if (
        distStatus.modified &&
        contentData.modified &&
        new Date(distStatus.modified) < new Date(contentData.modified)
      ) {
        // COMPLETED but modified date is earlier, content is not reflected
        serviceStatusMessages.push(
          `${service.name}: Newly modified content has not been reflected`
        );
      } else if (
        distStatus.modified &&
        contentData.modified &&
        new Date(distStatus.modified) >= new Date(contentData.modified)
      ) {
        // COMPLETED and modified date is same or later, content is reflected
        serviceStatusMessages.push(`${service.name}: Newly modified content has been reflected`);
      }
    }
  });

  return {
    serviceStatusMessages,
  };
};
